import { useCallback, useEffect, useRef, useState } from "react";

export const ENABLE_TYPING_PREVIEW = true;

export const TYPING_PREVIEW = {
  startDelayMs: 90,
  tickMs: 45,
  tokensPerTick: 2,
  beforeBlockMs: 260,
  blockDwellMs: 420,
} as const;

/* Pacing for the live stream. The server flushes text in bursts (dozens of tokens inside a
   single frame, then a pause), so revealing deltas as they land looks like paragraphs popping
   in. Draining a backlog at a steady rate turns that into a smooth type-out. */
export const STREAM_REVEAL = {
  tickMs: 24,
  catchUpDivisor: 12,
} as const;

export interface StreamReveal {
  visible: string;
  settled: boolean;
}

/* Markdown cut at an arbitrary character renders broken: an unclosed ``` fence turns the rest of
   the answer into a code block, half a `**bold` span shows raw asterisks, and a partial table row
   shows raw pipes. Backs the cut off to the last point that parses the same as the finished text. */
const occurrences = (text: string, needle: string) => text.split(needle).length - 1;

/* One retreat step: where the cut must move back to so it parses like the finished text will,
   or -1 when the cut is already safe. */
function retreatPoint(cut: string): number {
  // Odd fence count: inside an open code block, which would render the rest as code.
  if (occurrences(cut, "```") % 2 === 1) return cut.lastIndexOf("```");

  const lineStart = cut.lastIndexOf("\n") + 1; // 0 until the cut contains a newline
  const line = cut.slice(lineStart);

  // Half-written `**bold` or `code` shows its raw markers.
  if (occurrences(line, "**") % 2 === 1) return lineStart + line.lastIndexOf("**");
  else if (occurrences(line, "`") % 2 === 1) return lineStart + line.lastIndexOf("`");
  // A partial table row renders as raw pipes until its whole line lands.
  else if (line.includes("|")) return lineStart;
  // Whole words only, so prose never flickers mid-word.
  else if (!/\s$/.test(cut)) return Math.max(0, cut.search(/\s\S*$/) + 1);
  else return -1;
}

// Retreat until the cut parses the same as the finished text will. Every step moves the cut
// strictly left, so this terminates; retreating to the marker rather than to the line start
// keeps already-revealed words on screen instead of pulling them back.
function safeEnd(text: string, shown: number): number {
  if (shown >= text.length) return text.length;
  if (shown <= 0) return 0;
  const next = retreatPoint(text.slice(0, shown));
  return next < 0 ? shown : safeEnd(text, next);
}

/**
 * Paces a live-streaming answer so bursty server chunks type out at a steady rate.
 * A message that never streamed (history, reduced motion) is returned whole and settled.
 */
export function useStreamingReveal(text: string, run: boolean): StreamReveal {
  const textRef = useRef(text);
  textRef.current = text;
  const runRef = useRef(run);
  runRef.current = run;

  // Latches on the first paced render. A message that never streamed (history, a finished
  // answer, reduced motion) must show whole, and must never start revealing on a later render.
  const pacedRef = useRef(run);
  if (run) pacedRef.current = true;

  const [shown, setShown] = useState(() => (run ? 0 : text.length));
  const shownRef = useRef(shown);
  shownRef.current = shown;
  const settledRef = useRef(false);

  useEffect(() => {
    if (!pacedRef.current || settledRef.current) return;
    const id = window.setInterval(() => {
      const total = textRef.current.length;
      // Caught up and nothing more is coming: stop ticking for good. Decided here rather than
      // inside the updater, which must stay pure (React may re-run or double-invoke it).
      if (shownRef.current >= total && !runRef.current) {
        settledRef.current = true;
        window.clearInterval(id);
        return;
      }
      setShown((prev) => {
        // The terminal answer replaces the streamed draft and can be shorter than what is
        // already revealed, so clamp rather than hold an offset past the end.
        if (prev >= total) return total;
        return prev + Math.max(1, Math.ceil((total - prev) / STREAM_REVEAL.catchUpDivisor));
      });
    }, STREAM_REVEAL.tickMs);
    return () => window.clearInterval(id);
  }, [run]);

  // Keep pacing after the stream ends so the tail types out instead of snapping to the
  // full answer, which is what made the bubble appear to jump on completion.
  if (!pacedRef.current) return { visible: text, settled: true };
  // settled tracks the raw offset: safeEnd only holds back a partial line, and once shown
  // reaches the end it returns the whole text, so the two agree at completion.
  return { visible: text.slice(0, safeEnd(text, shown)), settled: !run && shown >= text.length };
}

export type MarkdownSegment =
  | { kind: "prose"; text: string; tokens: string[] }
  | { kind: "block"; text: string };

const TABLE_SEP = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/;
const TABLE_ROW = /^\s*\|.*\|\s*$/;
const FENCE = /^```/;

export function tokenize(text: string): string[] {
  return text.split(/(\s+)/).filter((t) => t.length > 0);
}

export function joinTokens(tokens: string[], count: number): string {
  return tokens.slice(0, Math.max(0, count)).join("");
}

export function segmentMarkdown(content: string): MarkdownSegment[] {
  const lines = content.replace(/\r\n/g, "\n").split("\n");
  const segments: MarkdownSegment[] = [];
  let prose: string[] = [];

  const flushProse = () => {
    if (!prose.length) return;
    const text = prose.join("\n");
    segments.push({ kind: "prose", text, tokens: tokenize(text) });
    prose = [];
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (FENCE.test(line.trim())) {
      flushProse();
      const buf = [line];
      i++;
      while (i < lines.length && !FENCE.test(lines[i].trim())) {
        buf.push(lines[i]);
        i++;
      }
      if (i < lines.length) {
        buf.push(lines[i]);
        i++;
      }
      segments.push({ kind: "block", text: buf.join("\n") });
      continue;
    }

    if (TABLE_ROW.test(line) && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1])) {
      flushProse();
      const buf = [line, lines[i + 1]];
      i += 2;
      while (i < lines.length && TABLE_ROW.test(lines[i])) {
        buf.push(lines[i]);
        i++;
      }
      segments.push({ kind: "block", text: buf.join("\n") });
      continue;
    }

    prose.push(line);
    i++;
  }
  flushProse();
  return segments;
}

export interface RevealPos {
  seg: number;
  tok: number;
}

export interface SegmentReveal {
  pos: RevealPos;
  done: boolean;
  skip: () => void;
}

export function useSegmentReveal(segments: MarkdownSegment[], run: boolean): SegmentReveal {
  const end = useCallback((): RevealPos => ({ seg: segments.length, tok: 0 }), [segments.length]);
  const [pos, setPos] = useState<RevealPos>(run ? { seg: 0, tok: 0 } : end());
  const [done, setDone] = useState(!run);
  const posRef = useRef<RevealPos>(pos);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  posRef.current = pos;

  const finish = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const e = { seg: segments.length, tok: 0 };
    posRef.current = e;
    setPos(e);
    setDone(true);
  }, [segments.length]);

  useEffect(() => {
    if (!run || segments.length === 0) {
      const e = { seg: segments.length, tok: 0 };
      posRef.current = e;
      setPos(e);
      setDone(true);
      return;
    }

    setDone(false);
    const start = { seg: 0, tok: 0 };
    posRef.current = start;
    setPos(start);
    let cancelled = false;

    const advance = () => {
      if (cancelled) return;
      const cur = posRef.current;
      const seg = segments[cur.seg];
      if (!seg) {
        setDone(true);
        return;
      }

      let next: RevealPos;
      if (seg.kind === "prose" && cur.tok < seg.tokens.length) {
        next = { seg: cur.seg, tok: Math.min(cur.tok + TYPING_PREVIEW.tokensPerTick, seg.tokens.length) };
      } else {
        const ni = cur.seg + 1;
        const nextSeg = segments[ni];
        const tok =
          nextSeg && nextSeg.kind === "prose"
            ? Math.min(TYPING_PREVIEW.tokensPerTick, nextSeg.tokens.length)
            : 0;
        next = { seg: ni, tok };
      }

      posRef.current = next;
      setPos(next);

      if (next.seg >= segments.length) {
        setDone(true);
        return;
      }

      const upcoming = segments[next.seg];
      let wait: number = TYPING_PREVIEW.tickMs;
      if (upcoming.kind === "block") {
        wait = TYPING_PREVIEW.beforeBlockMs;
      } else if (seg.kind === "block") {
        wait = TYPING_PREVIEW.blockDwellMs;
      }
      timerRef.current = setTimeout(advance, wait);
    };

    timerRef.current = setTimeout(advance, TYPING_PREVIEW.startDelayMs);
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [run, segments]);

  return { pos, done, skip: finish };
}
